import styled from '@emotion/styled';
import colors from 'lib/colors';
import { Children, useRef } from 'react';
import { useClickAway } from 'react-use';
import Flex from './Flex';

interface Props extends DropDownContentProps {
  toggleOptions: () => void;
  toggleButtonContent: React.ReactNode;
  children: React.ReactNode;
}

const DropDown = ({
  opened,
  toggleOptions,
  toggleButtonContent,
  children,
  ...props
}: Props) => {
  const ref = useRef(null);
  useClickAway(ref, () => opened && toggleOptions());

  return (
    <Flex flexDirection="column" ref={ref}>
      <DropDownButton onClick={toggleOptions} narrow={props.narrow}>
        {toggleButtonContent}
      </DropDownButton>
      <DropDownContent opened={opened} {...props}>
        {Children.map(children, (child, i) => (
          <li key={i}>{child}</li>
        ))}
      </DropDownContent>
    </Flex>
  );
};

export default DropDown;

interface DropDownButtonProps {
  narrow?: boolean;
}

const DropDownButton = styled('button')<DropDownButtonProps>(({ narrow }) => ({
  border: 'none',
  outline: 'none',
  pointerEvents: 'auto',
  backgroundColor: 'transparent',
  padding: 0,
  marginLeft: narrow ? 'auto' : 'unset',
}));

interface DropDownContentProps {
  opened: boolean;
  backgroundColor?: string;
  narrow?: boolean;
}

const DropDownContent = styled('ul')<DropDownContentProps>(
  ({ opened, backgroundColor, narrow }) => ({
    margin: 0,
    marginTop: -1,
    listStyle: 'none',
    width: '100%',
    backgroundColor: backgroundColor ? backgroundColor : colors.darkBlue,
    textAlign: 'right',
    letterSpacing: 0,
    padding: narrow ? '0' : '1em 0 1em 2em',
    li: {
      margin: narrow ? '.5em 1em' : '12px 30px 12px 0px',
      transition: 'all .1s ease-out',
      outline: 'none',
      ':hover': {
        transform: 'translateY(-1px) scale(1.03)',
      },
    },
    transformOrigin: 'top',
    overflow: 'hidden',
    transform: opened ? 'scaleY(1)' : 'scaleY(0)',
    pointerEvents: opened ? 'auto' : 'none',
    transition: 'transform .3s ease-out',
  }),
);
